import { useCallback, useEffect } from "react";
import { getEQM1010_R01List, exportEQM1010_R01Excel, getLatestEQM1010_R01 } from "./Api_EQM1010_R01";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { ShowSuccessFetch, showWarning } from "src/utils/toast/toastUtil";
import { downloadExcel } from "src/utils/blob/blobUtil";
import { useEQM1010_R01Store, defaultEQM1010_R01Filter, EQM1010_R01Filter } from "./Store_EQM1010_R01";
import { EQM1010_R01Dto } from "./Dto_EQM1010_R01";
import { formatDate } from "src/utils/string/stringUtil";
// import { fetchDataSetHandler, fetchFilterConfig } from "src/utils/grid/GridHandlerContent"; // 🔧 제거: 더 이상 사용하지 않음


interface EQM1010_R01ContentProps {
    setMessage: (message: string) => void;
}

export const Content_EQM1010_R01 = (props: EQM1010_R01ContentProps) => {
    const {
		setMessage,
    } = props;

    const { 
		EQM1010_R01Filter, setEQM1010_R01Filter,
		EQM1010_R01PastFilter, setEQM1010_R01PastFilter,
		EQM1010_R01List, setEQM1010_R01List, 
		selectedEQM1010_R01List,
		setAllCount,
		initData,
        latestDataList, setLatestDataList,
		setScrollTop,
	} = useEQM1010_R01Store();
	const { setLoadingSpinner } = useLoadingSpinnerStore();

	useFetchActionbarAuth("EQM1010_R01");

    const getParams = (filter: Partial<EQM1010_R01Filter>) => {
        return {
            compCd: filter.compCd,
            plcaddressid: filter.plcaddressid,
            baseDate: filter.baseDate,
        };
    }



	const isSameDate = (a?: Date, b?: Date) => {
		if (!a && !b) return true;
		if (!a || !b) return false;
		return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
	};

	const isSameFilter = (a: EQM1010_R01Filter, b: EQM1010_R01Filter) => {
		return (
			a.compCd === b.compCd &&
			a.plcaddressid === b.plcaddressid &&
			isSameDate(a.baseDate, b.baseDate)
		);
	};

	const fetchData = useCallback(async (isAutoRefresh: boolean = false) => {
        setLoadingSpinner(true);
        try {
			// 필터 비교: 같으면 더보기, 다르면 초기화 후 새조회
			// 단, 자동 재조회일 때는 항상 새로 조회 (데이터 업데이트)
			const same = isSameFilter(EQM1010_R01Filter, EQM1010_R01PastFilter);
			const isAppend = same && !isAutoRefresh;
			const nextListLength = isAppend ? EQM1010_R01List.length : 0;

			if (!isAppend) {
				setScrollTop(null);
			}

			// 화면 즉시 초기화: 필터 변경 시 또는 자동 재조회 시 기존 리스트 비움
			if ((!same || isAutoRefresh) && EQM1010_R01List.length > 0) {
				setEQM1010_R01List([]);
			}

            // 일반 데이터 조회와 최신 데이터 조회를 병렬로 실행
            const [response, latestResponse] = await Promise.all([
                getEQM1010_R01List(
                    '',
                    getParams(EQM1010_R01Filter),
                    nextListLength
                ),
                // 최신 데이터 조회 (baseDate가 있을 때만)
                EQM1010_R01Filter.baseDate 
                    ? getLatestEQM1010_R01(formatDate(EQM1010_R01Filter.baseDate))
                    : Promise.resolve({ data: { data: [] } } as any)
            ]);

            const rawData = response.data.data as EQM1010_R01Dto[];
            
			// 서버에서 반환된 데이터가 없으면 안내
            if (rawData.length === 0) {
				showWarning('조회된 데이터가 없습니다.');
                setLoadingSpinner(false);
                return;
            }
 
			// 필터 동일이고 수동 조회: append, 필터 변경 또는 자동 재조회: replace
			const newList = isAppend ? [...EQM1010_R01List, ...rawData] : [...rawData];
            setEQM1010_R01List(newList);

            // 최신 데이터 저장 (createdatetime 문자열을 Date 객체로 변환)
            if (latestResponse?.data?.data && latestResponse.data.data.length > 0) {
                const latestData = latestResponse.data.data.map((item: EQM1010_R01Dto) => ({
                    ...item,
                    createdatetime: item.createdatetime
                }));
                setLatestDataList(latestData);
            } else {
                setLatestDataList(null);
            }

			setEQM1010_R01PastFilter(EQM1010_R01Filter);
			ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${newList.length}건)`);
        } catch (error) {
            console.error('데이터 조회 실패:', error);
            showWarning('데이터 조회에 실패했습니다.');
        } finally {
            setLoadingSpinner(false);
        }
	}, [setLoadingSpinner, isSameFilter, EQM1010_R01Filter, EQM1010_R01PastFilter, EQM1010_R01List, setEQM1010_R01List, setEQM1010_R01PastFilter, setMessage, setScrollTop, setLatestDataList]);

    // 1분 자동조회
	useEffect(() => {
		if (!latestDataList || latestDataList.length === 0) return;

		const intervalId = setInterval(() => {
			fetchData(true);
		}, 60000);

		return () => clearInterval(intervalId);
	}, [latestDataList, fetchData]);
    
    const handleExcelClick = useCallback(async () => {
        try {
            const response = await exportEQM1010_R01Excel(
                '',
                getParams(EQM1010_R01Filter),
                0  // listLength를 0으로 설정하여 항상 새로 조회
            );

            const binaryString = atob(response.data.data);
            const byteArray = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
                byteArray[i] = binaryString.charCodeAt(i);
            }
            const blob = new Blob([byteArray]);
            downloadExcel(blob, '설비데이터 수집 조회-excel.xlsx');
        } catch (error) {
            console.error('엑셀 다운로드 실패:', error);
            showWarning('엑셀 다운로드에 실패했습니다.');
        }
    }, [EQM1010_R01Filter]);

    //  초기화 핸들러
    const handleInitClick = useCallback(() => {
        initData();
        setMessage('데이터가 초기화되었습니다.');
    }, [initData, setMessage]);

    // 최신 데이터만 조회하는 함수 (1분마다 자동 호출용)
    const fetchLatestData = useCallback(async () => {
        try {
            // baseDate가 있을 때만 최신 데이터 조회
            if (EQM1010_R01Filter.baseDate) {
                const latestResponse = await getLatestEQM1010_R01(formatDate(EQM1010_R01Filter.baseDate));
                
                if (latestResponse?.data?.data && latestResponse.data.data.length > 0) {
                    // createdatetime 문자열을 Date 객체로 변환
                    const latestData = latestResponse.data.data.map((item: EQM1010_R01Dto) => ({
                        ...item,
                        createdatetime: item.createdatetime
                    }));
                    setLatestDataList(latestData);
                } else {
                    setLatestDataList(null);
                }
            }
        } catch (error) {
            console.error('최신 데이터 조회 실패:', error);
            // 자동 조회는 조용히 실패 처리 (토스트 메시지 없음)
        }
    }, [EQM1010_R01Filter.baseDate, setLatestDataList]);
    
    return {
        fetchData,
        fetchLatestData,
        handleExcelClick,
        handleInitClick,
        // handlePrintClick,
        // handlePrintDetailClick,
    };

};
